<template>
    <div class="code-search">
        <div class="code-search__field">
            <div class="code-search__input-wrapper">
                <input
                    ref="codeInput"
                    type="text"
                    class="code-search__input"
                    :value="query"
                    @input="onInput($event.target.value)"
                    @keyup.enter="onSubmit"
                    placeholder="Kod dostępu / osoba kontaktowa"
                    :disabled="isCameraActive"
                />
                
                <button
                    v-if="query"
                    class="code-search__clear-btn"
                    @click="clearSearch"
                    title="Wyczyść"
                >
                    &times;
                </button>
            </div>
            <button
                    class="button is-primary code-search__scan-btn"
                    @click="toggleCamera"
                    :class="{'code-search__scan-btn--active': isCameraActive}"
                    title="Skanuj kod kamerą"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                        <circle cx="12" cy="13" r="4"/>
                    </svg>
                    skanuj
                </button>
        </div>

        <!-- Status indicators -->
        <div class="code-search__status" v-if="status !== 'idle' || isOffline">
            <div class="code-search__status-item code-search__status-item--scanning" v-if="isCameraActive">
                <span class="code-search__status-dot code-search__status-dot--red"></span>
                Skanowanie...
            </div>
            <div class="code-search__status-item code-search__status-item--searching" v-else-if="status === 'searching'">
                <span class="code-search__loader"></span>
                Szukam...
            </div>
            <div class="code-search__status-item code-search__status-item--offline" v-else-if="status === 'offline' || isOffline">
                <span class="code-search__status-dot code-search__status-dot--orange"></span>
                Tryb offline — filtrowanie lokalne
            </div>
            <!-- <div class="code-search__status-item code-search__status-item--no-results" v-else-if="status === 'noResults'">
                Brak wyników dla „{{ query }}"
            </div> -->
            <div class="code-search__status-item code-search__status-item--error" v-else-if="status === 'error'">
                {{ searchError || 'Błąd wyszukiwania' }}
            </div>
        </div>

        <!-- Inline scanner -->
            <div v-if="isCameraActive" class="code-search__camera" @click.self="closeCamera">
                <the-barcode-scanner
                    :inline="true"
                    :auto-stop="true"
                    @detected="onBarcodeDetected"
                    @scanning="onScanningChange"
                    @error="onScannerError"
                    ref="scanner"
                />
            </div>
    </div>
</template>

<script>
import TheBarcodeScanner from '@/components/TheBarcodeScanner'

export default {
    name: 'CodeSearchField',
    components: {
        'the-barcode-scanner': TheBarcodeScanner
    },
    data: function () {
        return {
            isCameraActive: false,
            debounceTimer: null
        }
    },
    computed: {
        query: function () {
            return this.$store.getters.getCodeSearchQuery
        },
        status: function () {
            return this.$store.getters.getCodeSearchStatus
        },
        searchError: function () {
            return this.$store.getters.getCodeSearchError
        },
        isOffline: function () {
            return this.$store.getters.getIsOffline
        }
    },
    methods: {
        onInput: function (value) {
            this.$store.dispatch('setCodeSearchQuery', value)
            // Debounce search
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer)
            }
            var self = this
            this.debounceTimer = setTimeout(function () {
                if (value && value.trim().length >= 2) {
                    self.performSearch(value.trim())
                } else if (!value || value.trim() === '') {
                    self.$store.dispatch('clearCodeSearch')
                }
            }, 500)
        },
        onSubmit: function () {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer)
            }
            var q = this.query.trim()
            if (q) {
                this.performSearch(q)
            }
        },
        performSearch: function (value) {
            // Determine if it looks like a code (numbers only, or EAN format) or a contact name
            if (/^\d+$/.test(value)) {
                this.$store.dispatch('searchByCode', value)
            } else {
                this.$store.dispatch('searchByContact', value)
            }
        },
        toggleCamera: function () {
            if (this.isCameraActive) {
                this.closeCamera()
            } else {
                this.openCamera()
            }
        },
        openCamera: function () {
            this.isCameraActive = true
            var self = this
            this.$nextTick(function () {
                if (self.$refs.scanner) {
                    self.$refs.scanner.startScanning()
                }
            })
        },
        closeCamera: function () {
            if (this.$refs.scanner) {
                this.$refs.scanner.stopScanning()
            }
            this.isCameraActive = false
        },
        onBarcodeDetected: function (barcode) {
            this.isCameraActive = false
            // Set the scanned value into the field and auto-submit
            this.$store.dispatch('setCodeSearchQuery', barcode.rawValue)
            this.$store.dispatch('searchByCode', barcode.rawValue)
            this.$store.dispatch('addNotification', {
                type: 'success',
                title: 'Kod zeskanowany',
                content: 'Wykryto kod: ' + barcode.rawValue
            })
        },
        onScanningChange: function (isScanning) {
            if (!isScanning) {
                this.isCameraActive = false
            }
        },
        onScannerError: function (error) {
            this.isCameraActive = false
            this.$store.dispatch('addNotification', {
                type: 'warning',
                title: 'Błąd skanera',
                content: error
            })
        },
        clearSearch: function () {
            this.closeCamera()
            this.$store.dispatch('clearCodeSearch')
        },
        handleOnlineStatus: function () {
            this.$store.dispatch('setOfflineStatus', !navigator.onLine)
        }
    },
    mounted: function () {
        window.addEventListener('online', this.handleOnlineStatus)
        window.addEventListener('offline', this.handleOnlineStatus)
        this.$store.dispatch('setOfflineStatus', !navigator.onLine)
    },
    beforeDestroy: function () {
        window.removeEventListener('online', this.handleOnlineStatus)
        window.removeEventListener('offline', this.handleOnlineStatus)
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }
        this.closeCamera()
    }
}
</script>

<style lang="sass" scoped>
@import "@/styles/variables"

.code-search
    margin-bottom: 1rem

    &__field
        display: flex
        align-items: center
        flex-wrap: wrap
        gap: 1rem

    &__input-wrapper
        display: flex
        align-items: center
        width: 100%
        border: 1px solid $grey
        border-radius: 4px
        background: $white
        overflow: hidden
        transition: border-color $fast
        padding: 0 1rem
        &:focus-within
            border-color: $green

    &__input
        flex: 1
        border: none
        outline: none
        padding: 0.6rem 0.75rem
        font-size: 1rem
        font-family: $family-primary
        background: transparent
        min-width: 0
        padding: 1.5rem
        &::placeholder
            color: $grey-dark
            opacity: 0.7

        &:disabled
            background: $grey-light
            color: $grey-dark

    &__scan-btn
        position: fixed
        bottom: 1rem
        left: 50%
        transform: translateX(-50%)
        display: flex
        flex-direction: column
        align-items: center
        justify-content: center
        width: 2.5rem
        // height: 2.5rem
        width: 100%
        max-width: 150px
        // padding: 2rem 1rem
        font-size: 0.87rem
        margin: 0 auto
        z-index: 99
        &:hover
            color: $green
        &--active
            color: $white
            background: $danger
        svg
            width: 24px
            height: 24px

    &__clear-btn
        display: flex
        align-items: center
        justify-content: center
        width: 2rem
        height: 2rem
        border: none
        background: transparent
        color: $grey-dark
        cursor: pointer
        font-size: 1.2rem
        line-height: 1
        flex-shrink: 0

        &:hover
            color: $danger

    &__status
        margin-top: 0.5rem

    &__status-item
        font-size: 1rem
        display: flex
        align-items: center
        padding: 0.25rem 0.5rem
        border-radius: 4px

        &--scanning
            color: $danger
            background: rgba($danger, 0.08)

        &--searching
            color: $green
            background: rgba($green, 0.08)

        &--offline
            color: $flashy-orange
            background: rgba($flashy-orange, 0.08)

        &--no-results
            color: $grey-dark
            background: rgba($grey-dark, 0.08)

        &--error
            color: $danger
            background: rgba($danger, 0.08)

    &__status-dot
        display: inline-block
        width: 8px
        height: 8px
        border-radius: 50%
        margin-right: 0.4rem

        &--red
            background: $danger
            animation: blink 1s infinite

        &--orange
            background: $flashy-orange

    &__loader
        display: inline-block
        width: 12px
        height: 12px
        border: 2px solid rgba($green, 0.3)
        border-top: 2px solid $green
        border-radius: 50%
        margin-right: 0.4rem
        animation: spin 0.8s linear infinite

    &__camera
        position: fixed
        top: 50%
        left: 0
        width: 100%
        margin-top: 0.5rem
        transform: translateY(-50%)
        z-index: 99
        &::before
            content: ''
            background: rgba(0, 0, 0, 0.5)
            position: fixed
            top: -200%
            left: 0
            width: 100%
            height: 300vh

@keyframes blink
    0%, 100%
        opacity: 1
    50%
        opacity: 0.3

@keyframes spin
    0%
        transform: rotate(0deg)
    100%
        transform: rotate(360deg)
</style>
